import { useState, useMemo, useEffect, useCallback } from "react";
import { TeamCategorySimple } from "../types/team_categories.types";

export function useTableControls<T>(
  data: T[],
  options?: {
    customFilters?: Record<
      string,
      (itemValue: any, selectedValues: string[]) => boolean
    >;
  }
) {
  const [sortConfig, setSortConfig] = useState<{
    sortBy: keyof T | null;
    sortDirection: "asc" | "desc" | null;
  }>({
    sortBy: null,
    sortDirection: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedFilters]);

  const normalizeString = (str: string) =>
    str.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const getNestedValue = (obj: any, path: string) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);

  const searchObjectValues = useCallback((obj: any, query: string): boolean => {
    if (!obj) return false;

    if (typeof obj === "string" || typeof obj === "number") {
      return normalizeString(String(obj)).includes(query);
    }

    if (Array.isArray(obj)) {
      return obj.some((item) => searchObjectValues(item, query));
    }

    if (typeof obj === "object") {
      return Object.values(obj).some((value) =>
        searchObjectValues(value, query)
      );
    }

    return false;
  }, []);

  const filteredData = useMemo(() => {
    let filtered = [...data];

    Object.entries(selectedFilters).forEach(([key, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter((item) => {
          const itemValue = getNestedValue(item, key);

          if (options?.customFilters?.[key]) {
            return options.customFilters[key](itemValue, values);
          }

          if (key === "categories") {
            return itemValue.some((category: TeamCategorySimple) =>
              values.includes(String(category.category_id))
            );
          } else {
            const normalizedItemValue = normalizeString(String(itemValue));
            return values.some(
              (filterValue) =>
                normalizedItemValue === normalizeString(filterValue)
            );
          }
        });
      }
    });

    if (searchQuery) {
      const normalizedQuery = normalizeString(searchQuery);
      filtered = filtered.filter((item) =>
        searchObjectValues(item, normalizedQuery)
      );
    }

    if (sortConfig.sortBy) {
      filtered.sort((a, b) => {
        const aValue = getNestedValue(a, sortConfig.sortBy as string);
        const bValue = getNestedValue(b, sortConfig.sortBy as string);
        return sortConfig.sortDirection === "asc"
          ? String(aValue).localeCompare(String(bValue), undefined, {
              numeric: true,
            })
          : String(bValue).localeCompare(String(aValue), undefined, {
              numeric: true,
            });
      });
    }

    return filtered;
  }, [
    data,
    searchQuery,
    sortConfig,
    selectedFilters,
    searchObjectValues,
    options,
  ]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  return {
    filteredData,
    paginatedData,
    totalPages,
    currentPage,
    sortConfig,
    searchQuery,
    selectedFilters,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
    normalizeString,
    getNestedValue,
  };
}
